import { observer } from "mobx-react";
import React from "react";
import { SettingsContainer } from "../../../../components/SettingsContainer";
import { TFunction } from "i18next";
import {
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
  RadioButtonColorTokens,
  DropdownInputField,
  DropdownItem,
  TextInputField,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { SettingsCheckboxComponent } from "./SettingsCheckboxComponent";
import {
  getRadioButtonStyleProps,
  showMaxCharacterCountIndicator,
} from "@pulse/shared-components";
import { SurveyRespondentHeaderTextTypeEnum } from "../../models/BackOfficeSurveySettingsModel";
import { RespondentColumnDetailsModel } from "../../../projects/models/RespondentColumnDetailsModel";
import { Instance } from "mobx-state-tree";
import {
  MAXIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH,
  MINIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH,
} from "../../../utils/constants";

interface RespondentColumnDropdownProps {
  selectedRespondentColumn: Instance<
    typeof RespondentColumnDetailsModel
  > | null;
  respondentColumns: DropdownItem[];
  surveyRespondentHeaderType: SurveyRespondentHeaderTextTypeEnum;
  setSelectedRespondentColumn: (selectedColumn: DropdownItem) => void;
  t: TFunction;
  doesRespondentColumnDropdownContainError: (
    respondentColumnForSurveyRespondentText: Instance<
      typeof RespondentColumnDetailsModel
    > | null,
  ) => boolean;
}

const RespondentColumnDropdown = observer(
  ({
    respondentColumns,
    selectedRespondentColumn,
    surveyRespondentHeaderType,
    setSelectedRespondentColumn,
    t,
    doesRespondentColumnDropdownContainError,
  }: RespondentColumnDropdownProps): React.ReactElement => {
    if (
      surveyRespondentHeaderType ===
      SurveyRespondentHeaderTextTypeEnum.RESPONDENT_COLUMN_WITH_FALLBACK_TEXT
    ) {
      return (
        <DropdownInputField
          name="respondentColumns"
          label={t(
            "surveys.settings.surveyHeaderTextSettings.respondentColumnsDropdownLabelText",
          )}
          value={
            selectedRespondentColumn?.index !== undefined
              ? `${selectedRespondentColumn?.index}`
              : undefined
          }
          options={respondentColumns}
          onSelect={(selectedColumn: DropdownItem): void => {
            setSelectedRespondentColumn(selectedColumn);
          }}
          placeholder={t(
            "surveys.settings.surveyHeaderTextSettings.respondentColumnsDropdownPlaceholder",
          )}
          isRequired={true}
          error={doesRespondentColumnDropdownContainError(
            selectedRespondentColumn,
          )}
          helperText={
            doesRespondentColumnDropdownContainError(selectedRespondentColumn)
              ? t(
                  "surveys.settings.surveyHeaderTextSettings.respondentColumnsDropdownValueNotSelectedHelperText",
                )
              : undefined
          }
        />
      );
    }
    return <></>;
  },
);

interface CustomFallbackTextInputFieldProps {
  customFallbackText: string | null;
  setCustomFallbackTextForSurveyRespondentHeaderText: (
    customFallbackText: string,
  ) => void;
  t: TFunction;
  surveyRespondentHeaderType: SurveyRespondentHeaderTextTypeEnum;
  doesCustomFallbackTextInputFieldContainError: (
    customFallbackTextForSurveyRespondentHeaderText: string | null,
  ) => boolean;
}

const CustomFallbackTextInputField = observer(
  ({
    customFallbackText,
    setCustomFallbackTextForSurveyRespondentHeaderText,
    t,
    surveyRespondentHeaderType,
    doesCustomFallbackTextInputFieldContainError,
  }: CustomFallbackTextInputFieldProps): React.ReactElement => {
    const label =
      surveyRespondentHeaderType ===
      SurveyRespondentHeaderTextTypeEnum.CUSTOM_TEXT
        ? t(
            "surveys.settings.surveyHeaderTextSettings.customTextInputFieldLabel",
          )
        : t(
            "surveys.settings.surveyHeaderTextSettings.fallbackTextInputFieldLabel",
          );

    const placeholder =
      surveyRespondentHeaderType ===
      SurveyRespondentHeaderTextTypeEnum.CUSTOM_TEXT
        ? t(
            "surveys.settings.surveyHeaderTextSettings.customTextInputFieldPlaceholder",
          )
        : t(
            "surveys.settings.surveyHeaderTextSettings.fallbackTextInputFieldPlaceholder",
          );
    if (
      surveyRespondentHeaderType !== SurveyRespondentHeaderTextTypeEnum.NONE
    ) {
      return (
        <TextInputField
          name="surveyName"
          value={customFallbackText ?? undefined}
          required={true}
          type="text"
          onTextChange={(input) =>
            setCustomFallbackTextForSurveyRespondentHeaderText(
              input.slice(
                0,
                MAXIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH,
              ),
            )
          }
          label={label}
          placeholder={placeholder}
          maxCharacterLength={
            MAXIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH
          }
          showCharacterCountIndicator={(): boolean =>
            showMaxCharacterCountIndicator(
              MAXIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH,
              customFallbackText ?? undefined,
            )
          }
          width="400px"
          error={doesCustomFallbackTextInputFieldContainError(
            customFallbackText,
          )}
          helperText={
            doesCustomFallbackTextInputFieldContainError(customFallbackText)
              ? t("surveys.settings.genericHelperText", {
                  minCharacterLength:
                    MINIMUM_CUSTOM_FALLBACK_TEXT_FOR_SURVEY_RESPONDENT_HEADER_LENGTH,
                })
              : undefined
          }
        />
      );
    }
    return <></>;
  },
);

interface SurveyRespondentHeaderTextSettingsProps {
  setIsSurveyRespondentHeaderTextVisible: (
    isSurveyRespondentHeaderTextVisible: boolean,
  ) => void;
  isSurveyRespondentHeaderTextVisible: boolean;
  surveyRespondentHeaderType: SurveyRespondentHeaderTextTypeEnum;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  setSelectedSurveyRespondentHeaderType: (
    selectedSurveyRespondentHeaderType: string,
  ) => void;
  radioButtonTokens: RadioButtonColorTokens<string>;
  selectedRespondentColumn: Instance<
    typeof RespondentColumnDetailsModel
  > | null;
  respondentColumns: DropdownItem[];
  setSelectedRespondentColumn: (selectedColumn: DropdownItem) => void;
  customFallbackTextForSurveyRespondentHeaderText: string | null;
  setCustomFallbackTextForSurveyRespondentHeaderText: (
    customFallbackTextForSurveyRespondentHeaderText: string,
  ) => void;
  doesCustomFallbackTextInputFieldContainError: (
    customFallbackTextForSurveyRespondentHeaderText: string | null,
  ) => boolean;
  doesRespondentColumnDropdownContainError: (
    respondentColumnForSurveyRespondentText: Instance<
      typeof RespondentColumnDetailsModel
    > | null,
  ) => boolean;
}

export const SurveyRespondentHeaderTextSettings = observer(
  ({
    isSurveyRespondentHeaderTextVisible,
    setIsSurveyRespondentHeaderTextVisible,
    t,
    spacing,
    typography,
    tokens,
    surveyRespondentHeaderType,
    setSelectedSurveyRespondentHeaderType,
    radioButtonTokens,
    respondentColumns,
    selectedRespondentColumn,
    setSelectedRespondentColumn,
    customFallbackTextForSurveyRespondentHeaderText,
    setCustomFallbackTextForSurveyRespondentHeaderText,
    doesCustomFallbackTextInputFieldContainError,
    doesRespondentColumnDropdownContainError,
  }: SurveyRespondentHeaderTextSettingsProps): React.ReactElement => {
    const radioProps = getRadioButtonStyleProps(
      tokens,
      radioButtonTokens,
      spacing,
      // Only horizontal padding is provided here because the vertical padding is given to the radio button as a whole.
      // Adding vertical padding here would add padding only to the radio button icon and not the text.
      `0px ${spacing.spaceXS}`,
    );
    return (
      <SettingsContainer
        settingHeadingText={t(
          "surveys.settings.surveyHeaderTextSettings.title",
        )}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
      >
        <Stack width="600px">
          <SettingsCheckboxComponent
            isCheckboxSelected={isSurveyRespondentHeaderTextVisible}
            setIsCheckboxSelected={setIsSurveyRespondentHeaderTextVisible}
            checkboxLabel={t(
              "surveys.settings.surveyHeaderTextSettings.showHeaderText",
            )}
            checkboxDescription={t(
              "surveys.settings.surveyHeaderTextSettings.showHeaderTextDescription",
            )}
            spacing={spacing}
            typography={typography}
            tokens={tokens}
          />
          <Stack gap={spacing.spaceLG}>
            <RadioGroup
              value={surveyRespondentHeaderType}
              onChange={(_, value) => {
                setSelectedSurveyRespondentHeaderType(value);
              }}
            >
              <Stack py={spacing.spaceXS} direction="row">
                <FormControlLabel
                  control={
                    <Radio
                      disabled={!isSurveyRespondentHeaderTextVisible}
                      {...radioProps}
                    />
                  }
                  value={
                    SurveyRespondentHeaderTextTypeEnum.RESPONDENT_COLUMN_WITH_FALLBACK_TEXT
                  }
                  label={
                    <Typography
                      {...typography.b1}
                      color={
                        !isSurveyRespondentHeaderTextVisible
                          ? tokens.labelSubtle
                          : tokens.label
                      }
                    >
                      {t(
                        "surveys.settings.surveyHeaderTextSettings.respondentColumnWithFallbackText",
                      )}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      disabled={!isSurveyRespondentHeaderTextVisible}
                      {...radioProps}
                    />
                  }
                  value={SurveyRespondentHeaderTextTypeEnum.CUSTOM_TEXT}
                  label={
                    <Typography
                      {...typography.b1}
                      color={
                        !isSurveyRespondentHeaderTextVisible
                          ? tokens.labelSubtle
                          : tokens.label
                      }
                    >
                      {t(
                        "surveys.settings.surveyHeaderTextSettings.customText",
                      )}
                    </Typography>
                  }
                />
              </Stack>
            </RadioGroup>
            <Stack direction="row" gap={spacing.spaceXL}>
              <RespondentColumnDropdown
                respondentColumns={respondentColumns}
                selectedRespondentColumn={selectedRespondentColumn}
                surveyRespondentHeaderType={surveyRespondentHeaderType}
                setSelectedRespondentColumn={setSelectedRespondentColumn}
                t={t}
                doesRespondentColumnDropdownContainError={
                  doesRespondentColumnDropdownContainError
                }
              />
              <CustomFallbackTextInputField
                customFallbackText={
                  customFallbackTextForSurveyRespondentHeaderText
                }
                setCustomFallbackTextForSurveyRespondentHeaderText={
                  setCustomFallbackTextForSurveyRespondentHeaderText
                }
                t={t}
                surveyRespondentHeaderType={surveyRespondentHeaderType}
                doesCustomFallbackTextInputFieldContainError={
                  doesCustomFallbackTextInputFieldContainError
                }
              />
            </Stack>
          </Stack>
        </Stack>
      </SettingsContainer>
    );
  },
);
